import { cn } from '@/lib/utils';
import type { USCCBReading } from '@/hooks/useUSCCBReadings';

interface ReadingBlockProps {
  reading: USCCBReading;
  colorHex?: string;       // kept for API compatibility, no longer used for styling
  liturgicalColor?: string; // kept for API compatibility, no longer used for styling
}

// All reading cards use the same neutral left bar — color comes from the thin top accent only
const LEFT_BAR_COLOR = 'bg-stone-200 dark:bg-stone-700';

function isResponsorialPsalm(label: string) {
  const l = label.toLowerCase();
  return l.includes('psalm') || l.includes('responsorial');
}

function isAlleluia(label: string) {
  const l = label.toLowerCase();
  return l.includes('alleluia') || l.includes('acclamation');
}

export function ReadingBlock({ reading, colorHex, liturgicalColor = 'green' }: ReadingBlockProps) {
  const isPsalm = isResponsorialPsalm(reading.label);
  const isAcclamation = isAlleluia(reading.label);

  const isFirstReading = reading.label.toLowerCase().includes('reading 1');
  const isGospel = reading.label.toLowerCase().includes('gospel') && !isAcclamation;
  const useDropcap = isFirstReading || isGospel;

  const paragraphs = reading.text
    .split(/\n{2,}|\n(?=[A-Z])/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 shadow-sm">
      <div className="flex">
        {/* Left bar — neutral stone */}
        <div className={cn('w-1 shrink-0', LEFT_BAR_COLOR)} />

        <div className="flex-1 px-5 pt-5 pb-6">
          {/* Header */}
          <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 className="text-[13px] font-bold tracking-[0.1em] uppercase text-stone-500 dark:text-stone-400" style={{ fontFamily: '"Cinzel", serif' }}>
              {reading.label}
            </h3>
            {reading.reference && (
              <span className="text-sm text-stone-500 dark:text-stone-400 italic">{reading.reference}</span>
            )}
          </div>

          {/* Responsorial Psalm — special verse/refrain layout */}
          {isPsalm ? (
            <PsalmBody paragraphs={paragraphs} />
          ) : isAcclamation ? (
            <AcclamationBody paragraphs={paragraphs} />
          ) : (
            /* Regular reading */
            <div className="space-y-4 text-[17px] leading-[1.85] text-stone-700 dark:text-stone-300">
              {paragraphs.length > 0 ? (
                paragraphs.map((para, i) => {
                  if (i === 0 && useDropcap) {
                    return <p key={i} className="dropcap">{para}</p>;
                  }
                  return <p key={i}>{para}</p>;
                })
              ) : (
                <p className="text-sm italic text-stone-400">
                  Reading text not available — please view on USCCB.org.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PsalmBody({ paragraphs }: { paragraphs: string[] }) {
  if (paragraphs.length === 0) {
    return <p className="text-sm italic text-stone-400">Text not available — please view on USCCB.org.</p>;
  }

  const isRefrain = (s: string) => /^R[.\s]/.test(s.trim());

  return (
    <div className="space-y-4">
      {paragraphs.map((para, i) => {
        if (isRefrain(para)) {
          return (
            <div key={i} className="rounded-lg border border-stone-200 dark:border-stone-700 px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500 mb-1">Refrain</p>
              <p className="text-[16px] leading-[1.8] font-semibold italic text-stone-800 dark:text-stone-200">
                {para.replace(/^R[.\s]\s*/, '')}
              </p>
            </div>
          );
        }
        return (
          <p key={i} className="text-[16px] leading-[1.85] text-stone-600 dark:text-stone-400 pl-1">
            {para}
          </p>
        );
      })}
    </div>
  );
}

function AcclamationBody({ paragraphs }: { paragraphs: string[] }) {
  if (paragraphs.length === 0) {
    return <p className="text-sm italic text-stone-400">Text not available — please view on USCCB.org.</p>;
  }

  const isRefrain = (s: string) => /^(Alleluia|R[.\s])/i.test(s.trim());

  return (
    <div className="space-y-3">
      {paragraphs.map((para, i) => {
        if (isRefrain(para)) {
          return (
            <div key={i} className="rounded-lg border border-stone-200 dark:border-stone-700 px-4 py-3 text-center">
              <p className="text-[17px] leading-[1.8] font-semibold text-stone-800 dark:text-stone-200 tracking-wide">
                {para}
              </p>
            </div>
          );
        }
        return (
          <p key={i} className="text-[16px] leading-[1.85] text-stone-500 dark:text-stone-400 italic text-center">
            {para}
          </p>
        );
      })}
    </div>
  );
}
